import React, { useMemo, useState } from 'react';
import { projects } from '../../Data';
import { FaExternalLinkAlt, FaGithub } from 'react-icons/fa';

function toTechTags(techStack) {
  if (!techStack) return [];
  return techStack
    .split(/•|·|,|\||\//g)
    .map((s) => s.trim())
    .filter(Boolean)
    .slice(0, 6);
}

const Portfolio = () => {
  const categories = useMemo(() => {
    const list = Array.from(new Set(projects.map((p) => p.category).filter(Boolean)));
    return ['All', ...list];
  }, []);

  const [active, setActive] = useState('All');

  const filtered = useMemo(() => {
    if (active === 'All') return projects;
    return projects.filter((p) => p.category === active);
  }, [active]);

  return (
    <section
      id="work"
      className="relative overflow-hidden bg-gradient-to-b from-[#0b0f17] to-[#070a10] py-20"
    >
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-24 left-1/2 h-[520px] w-[520px] -translate-x-1/2 rounded-full bg-gradient-to-tr from-accent/18 via-emerald-500/10 to-transparent blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-6 relative">
        <div className="text-center mb-12 md:mb-16">
          <p className="text-xs tracking-[0.22em] uppercase text-gray-400">Portfolio</p>
          <h2 className="mt-3 text-3xl md:text-4xl font-bold text-white tracking-tight">
            Featured Projects
          </h2>
          <p className="mt-4 text-sm md:text-base text-gray-400 max-w-2xl mx-auto leading-relaxed">
            A selection of projects I’ve worked on.
          </p>
        </div>

        {/* Filter pills */}
        <div className="flex flex-wrap justify-center gap-3 mb-10">
          {categories.map((c) => {
            const isActive = c === active;
            return (
              <button
                key={c}
                type="button"
                onClick={() => setActive(c)}
                className={[
                  'px-4 py-1.5 rounded-full text-sm font-semibold transition-all',
                  'border border-white/10 bg-white/5 text-white/75 hover:text-white hover:bg-white/10',
                  isActive ? 'bg-accent text-black border-accent/40 shadow-soft' : '',
                ].join(' ')}
              >
                {c}
              </button>
            );
          })}
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filtered.map((p) => {
            const tech = toTechTags(p.techStack);
            return (
              <article
                key={p.id}
                className="group rounded-2xl overflow-hidden border border-white/10 bg-white/5 backdrop-blur shadow-soft transition-transform duration-300 hover:scale-[1.02] hover:shadow-2xl hover:border-accent/40"
              >
                {/* image */}
                <a
                  href={p.linkUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="relative block aspect-video overflow-hidden"
                  aria-label={`Open live demo: ${p.title}`}
                >
                  <img
                    src={p.img}
                    alt={`${p.title} — project by Yogesh Singh`}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                    decoding="async"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent opacity-70 transition-opacity duration-300 group-hover:opacity-90" />
                </a>

                {/* content */}
                <div className="p-6 space-y-3">
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center rounded-full bg-white/10 px-2.5 py-1 text-xs font-semibold text-white/80">
                      {p.category}
                    </span>
                    {p.impact && (
                      <span className="text-xs text-gray-400 truncate">
                        {p.impact}
                      </span>
                    )}
                  </div>

                  <h3 className="text-lg font-semibold text-white tracking-tight">
                    {p.title}
                  </h3>

                  <p className="text-sm text-gray-400 leading-relaxed">
                    {p.description}
                  </p>

                  {tech.length > 0 && (
                    <div className="flex flex-wrap gap-2 pt-1">
                      {tech.map((t) => (
                        <span
                          key={`${p.id}-${t}`}
                          className="px-2 py-1 rounded-full bg-white/10 text-xs font-medium text-gray-200 border border-white/10"
                        >
                          {t}
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="pt-2 flex items-center gap-3">
                    <a
                      href={p.linkUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-sm font-semibold text-white/85 hover:text-white transition-colors"
                    >
                      <FaExternalLinkAlt className="h-3.5 w-3.5 text-accent" />
                      Live Demo
                    </a>

                    {p.githubUrl && (
                      <a
                        href={p.githubUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-sm font-semibold text-white/85 hover:text-white transition-colors"
                      >
                        <FaGithub className="h-4 w-4 text-accent" />
                        GitHub
                      </a>
                    )}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Portfolio;